import { useEffect, useState } from "react";
import api, { fmtError } from "@/lib/api";
import { uploadMedia } from "@/lib/media";
import { invalidateManufacturers } from "@/hooks/useManufacturers";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Building2, Pencil, Globe, ImagePlus } from "lucide-react";

function CompanyDialog({ open, onOpenChange, initial, onSaved }) {
  const isEdit = !!initial?.id;
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(initial ? { ...initial } : { is_active: true, display_order: 0 });
  }, [open, initial]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const m = await uploadMedia(file);
      set("logo_url", m.url);
      toast.success("Logo uploaded");
    } catch (err) { toast.error(fmtError(err)); } finally { setUploading(false); e.target.value = ""; }
  };

  const submit = async () => {
    if (!(form.name || "").trim()) { toast.error("Company name required"); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        code: form.code || null,
        website: form.website || null,
        description: form.description || null,
        logo_url: form.logo_url || null,
        display_order: Number(form.display_order) || 0,
        is_active: form.is_active !== false,
        notes: form.notes || null,
      };
      if (isEdit) await api.patch(`/manufacturers/${initial.id}`, payload);
      else await api.post("/manufacturers", payload);
      invalidateManufacturers();
      toast.success(isEdit ? "Company updated" : "Company created");
      onSaved?.();
      onOpenChange(false);
    } catch (err) { toast.error(fmtError(err)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-3xl max-w-lg max-h-[90vh] overflow-y-auto" data-testid="company-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-extrabold">{isEdit ? "Edit company" : "New company"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <div className="h-16 w-16 rounded-2xl border border-border/60 bg-muted/30 grid place-items-center overflow-hidden shrink-0">
              {form.logo_url
                ? <img src={form.logo_url} alt="logo" className="h-full w-full object-contain" />
                : <Building2 className="h-6 w-6 text-muted-foreground" />}
            </div>
            <div>
              <label className="inline-flex items-center gap-1.5 px-3 h-9 rounded-2xl border border-border/60 text-xs font-semibold cursor-pointer hover:bg-muted/30">
                <ImagePlus className="h-3.5 w-3.5" /> {uploading ? "Uploading…" : (form.logo_url ? "Change logo" : "Upload logo")}
                <input type="file" accept="image/*" className="hidden" onChange={onLogo} data-testid="company-logo-input" />
              </label>
              {form.logo_url && (
                <button type="button" className="block text-[10px] underline text-muted-foreground mt-1" onClick={() => set("logo_url", "")}>Remove logo</button>
              )}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="col-span-2 sm:col-span-1">
              <Label className="text-xs font-semibold">Company name *</Label>
              <Input className="rounded-2xl h-11 mt-1.5" value={form.name || ""} onChange={(e) => set("name", e.target.value)} data-testid="company-name-input" />
            </div>
            <div className="col-span-2 sm:col-span-1">
              <Label className="text-xs font-semibold">Company code</Label>
              <Input className="rounded-2xl h-11 mt-1.5" value={form.code || ""} onChange={(e) => set("code", e.target.value)} placeholder="e.g. UES" data-testid="company-code-input" />
            </div>
          </div>
          <div>
            <Label className="text-xs font-semibold">Website</Label>
            <Input className="rounded-2xl h-11 mt-1.5" value={form.website || ""} onChange={(e) => set("website", e.target.value)} placeholder="https://…" data-testid="company-website-input" />
          </div>
          <div>
            <Label className="text-xs font-semibold">Description</Label>
            <Textarea className="rounded-2xl mt-1.5" rows={3} value={form.description || ""} onChange={(e) => set("description", e.target.value)} data-testid="company-description-input" />
          </div>
          <div className="flex items-center justify-between gap-3">
            <div className="flex-1">
              <Label className="text-xs font-semibold">Display order</Label>
              <Input type="number" className="rounded-2xl h-11 mt-1.5 w-28" value={form.display_order ?? 0} onChange={(e) => set("display_order", e.target.value)} data-testid="company-order-input" />
            </div>
            <div className="flex items-center gap-2 pt-5">
              <Label className="text-xs font-semibold">Active</Label>
              <Switch checked={form.is_active !== false} onCheckedChange={(v) => set("is_active", v)} data-testid="company-active-switch" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-2xl" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving || uploading} className="rounded-2xl" data-testid="company-save-button">{saving ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export const CompanyManager = ({ canManage, productCounts = {} }) => {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/manufacturers", { params: { include_archived: true } }).then((r) => setRows(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const toggleActive = async (m) => {
    try {
      await api.patch(`/manufacturers/${m.id}`, { name: m.name, is_active: m.is_active === false, archived: false });
      invalidateManufacturers();
      load();
      toast.success(m.is_active === false ? "Company activated" : "Company deactivated");
    } catch (err) { toast.error(fmtError(err)); }
  };

  return (
    <div data-testid="company-manager">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-muted-foreground">Companies (OEMs) shown in the catalog and quotation picker. Deactivated companies stay on old quotations but can't be picked for new ones.</p>
        {canManage && (
          <Button onClick={() => { setEditing(null); setOpen(true); }} className="rounded-2xl shrink-0" data-testid="new-company-button">
            <Plus className="h-4 w-4 mr-1" /> New company
          </Button>
        )}
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {rows.map((m) => {
          const inactive = m.is_active === false || m.archived === true;
          return (
            <Card key={m.id} className={`rounded-3xl border-border/60 shadow-soft ${inactive ? "opacity-60" : ""}`} data-testid={`company-card-${m.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <div className="h-12 w-12 rounded-2xl border border-border/60 bg-muted/30 grid place-items-center overflow-hidden shrink-0">
                    {m.logo_url ? <img src={m.logo_url} alt={m.name} className="h-full w-full object-contain" /> : <Building2 className="h-5 w-5 text-muted-foreground" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-display font-extrabold truncate">{m.name}</p>
                    <p className="text-xs text-muted-foreground truncate">
                      {[m.code, `${productCounts[m.id] || 0} product${(productCounts[m.id] || 0) === 1 ? "" : "s"}`].filter(Boolean).join(" · ")}
                    </p>
                  </div>
                  <Badge variant="outline" className={`text-[10px] ${inactive ? "bg-stone-100 text-stone-500 border-stone-200" : "bg-primary-50 text-primary-700 border-primary-200"}`}>
                    {inactive ? "Inactive" : "Active"}
                  </Badge>
                </div>
                {m.description && <p className="text-xs text-muted-foreground mt-2 line-clamp-2">{m.description}</p>}
                {m.website && (
                  <a href={m.website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-primary-700 underline mt-1.5">
                    <Globe className="h-3 w-3" /> Website
                  </a>
                )}
                {canManage && (
                  <div className="mt-3 pt-2 border-t border-border/40 flex items-center justify-between">
                    <Button variant="ghost" size="sm" className="rounded-xl h-7 text-xs" onClick={() => { setEditing(m); setOpen(true); }} data-testid={`company-edit-${m.id}`}>
                      <Pencil className="h-3 w-3 mr-1" /> Edit
                    </Button>
                    <Button variant="ghost" size="sm" className={`rounded-xl h-7 text-xs ${inactive ? "text-primary-700" : "text-red-600"}`} onClick={() => toggleActive(m)} data-testid={`company-toggle-${m.id}`}>
                      {inactive ? "Activate" : "Deactivate"}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
      <CompanyDialog open={open} onOpenChange={setOpen} initial={editing} onSaved={load} />
    </div>
  );
};
